<template>
  <el-dialog v-model="visible" title="角色签名" width="560px" destroy-on-close>
    <el-alert
      title="未设置角色签名时使用本人签名，清空后恢复使用本人签名。"
      type="info"
      :closable="false"
      show-icon
      style="margin-bottom: 20px"
    />
    <el-form label-width="90px">
      <el-form-item label="角色">
        <el-select v-model="roleCode" :disabled="saving" @change="changeRole">
          <el-option
            v-for="role in userStore.currRoles ?? []"
            :key="role.roleCode"
            :value="role.roleCode"
            :label="role.roleName"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="角色签名">
        <div>
          <el-image
            v-if="signUrl"
            :src="signUrl"
            fit="contain"
            style="
              width: 270px;
              height: 90px;
              display: block;
              margin-bottom: 12px;
            "
          />
          <el-text
            v-else
            type="info"
            style="display: block; margin-bottom: 12px"
          >
            未设置，使用本人签名
          </el-text>
          <el-upload
            accept="image/png,image/jpeg"
            :auto-upload="false"
            :show-file-list="false"
            :disabled="saving || !roleCode"
            :on-change="chooseImage"
            style="display: inline-block"
          >
            <el-button :disabled="saving || !roleCode">上传签名</el-button>
          </el-upload>
          <el-button
            :disabled="saving || !signUrl"
            @click="clearSign"
            style="margin-left: 12px"
          >
            清空
          </el-button>
        </div>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button :disabled="saving" @click="visible = false">取消</el-button>
      <el-button
        type="primary"
        :loading="saving"
        :disabled="!roleCode"
        @click="save"
      >
        保存
      </el-button>
    </template>
  </el-dialog>
  <ele-cropper-modal
    v-model="cropperVisible"
    :src="cropperSrc"
    :options="{ aspectRatio: 3, autoCropArea: 1, viewMode: 0 }"
    :to-blob="true"
    :modal-props="{ destroyOnClose: true }"
    @done="onCrop"
  />
</template>

<script lang="ts" setup>
  import { onBeforeUnmount, ref } from 'vue';
  import type { UploadFile } from 'element-plus';
  import { EleMessage } from 'ele-admin-plus/es';
  import { useUserStore } from '@/store/modules/user';
  import { saveTeacherProp } from '@/api/bsl/tenant/user/teacher/prop';
  import fileUtil, { FileUploadPkConfig } from '@/utils/file-util';

  const visible = defineModel({ type: Boolean });
  const userStore = useUserStore();
  const roleCode = ref(
    userStore.currRole?.roleCode ?? userStore.currRoles?.[0]?.roleCode ?? ''
  );
  const signUrl = ref('');
  const saving = ref(false);
  const cropperVisible = ref(false);
  const cropperSrc = ref('');
  let signatureFile: File | null = null;
  let objectUrl = '';

  const releaseImage = () => {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    objectUrl = '';
  };
  const changeRole = () => {
    releaseImage();
    signatureFile = null;
    signUrl.value =
      userStore.teacherProps.find(
        (prop) => prop.propId === `${roleCode.value}_sign_url`
      )?.propValue ?? '';
  };
  const chooseImage = (file: UploadFile) => {
    if (!file.raw || !['image/png', 'image/jpeg'].includes(file.raw.type)) {
      EleMessage.warning('请选择 PNG 或 JPG 格式的签名图片');
      return;
    }
    releaseImage();
    objectUrl = URL.createObjectURL(file.raw);
    cropperSrc.value = objectUrl;
    cropperVisible.value = true;
  };
  const onCrop = (blob: Blob) => {
    cropperVisible.value = false;
    releaseImage();
    signatureFile = new File([blob], 'role-sign.png', { type: blob.type });
    objectUrl = URL.createObjectURL(signatureFile);
    signUrl.value = objectUrl;
  };
  const clearSign = () => {
    releaseImage();
    signatureFile = null;
    signUrl.value = '';
  };
  const save = async () => {
    saving.value = true;
    try {
      const url = signatureFile
        ? (
            await fileUtil.uploadFile(
              FileUploadPkConfig.signatureImage,
              signatureFile
            )
          ).fileUrl
        : signUrl.value;
      const prop = await saveTeacherProp(`${roleCode.value}_sign_url`, url);
      userStore.setTeacherProp(prop);
      EleMessage.success('角色签名保存成功');
      visible.value = false;
    } catch (error: any) {
      EleMessage.error(error.message);
    } finally {
      saving.value = false;
    }
  };
  changeRole();
  onBeforeUnmount(releaseImage);
</script>
